import React from "react";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import {
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@material-ui/core";
import { observer } from "mobx-react";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";

import ColumnConfigFactory from "./ColumnConfigFactory";
import styled from "styled-components";

import ColumnTypesListPopup, {
  columnTypesArrayPlain,
} from "./ColumnTypesListPopup";
import { useState } from "react";
import UniqueOptions from "../AddColumn/Columns/common/UniqueOptions";

const getDefaultColumnOptions = (columnTypeId = 1) => {
  if(
    columnTypeId === 3
  ){
    return {
      is_read_only: false,
      is_mandatory: false,
      is_filter: false,
      is_allow_non_admin: columnTypeId === 3 ? false : undefined
    }
  }
  if (
    columnTypeId === 13 ||
    columnTypeId === 11 ||
    columnTypeId === 9
  ) {
    return {
      is_read_only: false,
      is_mandatory: false,
      is_filter: false,
    };
  } else if (columnTypeId === 12) {
    return {
      is_read_only: false,
      is_filter: false,
    };
  } else if (columnTypeId == 1 || columnTypeId == 5) {
    return {
      default_value :"",
      is_read_only: false,
      is_mandatory: false,
      is_unique: false,
      unique_options: null,
      is_filter: false,
      trim_white_spaces: true
    };
  }
  return {
    is_read_only: false,
    is_mandatory: false,
    is_unique: false,
    unique_options: null,
    is_filter: false,
    trim_white_spaces: true
  };
};

const getDefaultUniqueOptions = (columnName = "", options) => ({
  action: options?.action ? options.action : "error",
  message: options?.action == "warning" || options?.action == "error" ? options.message 
    : `Duplicate values not permitted for ${columnName}`,
  errorMessage: `Duplicate values not permitted for ${columnName}`,
  warningMessage:`Duplicate values for ${columnName}. Are you sure you want to proceed?`,
});

function AddColumnForm({
  column,
  handleSubmit,
  getCommonColumnOptions,
  handleClose,
  store,
  forceUpdate
}) {
  const {
    isSubsheet = false,
    appStore: { userListId, userListColumns },
  } = store;
  const [isAdditionalOptions, showAdditionalOptions] = useState(false);
  
  if (column?.column_type_id === "10" && column.options.is_user_column) {
    column.column_type_id = "10-1";
    // column.is_user_type = true;
  }

  return (
    <Formik
      initialValues={{
        column_id: 0,
        column_name: "",
        column_type_id: 1,
        formula: "",
        options: getDefaultColumnOptions(),
        is_deleted: false,
        ...column,
      }}
      onSubmit={handleSubmit}
      validate={(values) => {
        const errors = {};

        if (!store.validateColumnName(values.column_name, values.column_id)) {
          errors.column_name = "Duplicate column names not allowed";
        }

        return errors;
      }}
      validationSchema={Yup.object().shape({
        column_name: Yup.string().required("Name is required"),
      })}
    >
      {(props) => {
        const {
          values,
          touched,
          errors,
          dirty,
          isSubmitting,
          handleChange,
          handleBlur,
          handleSubmit,
          handleReset,
          setFieldValue,
        } = props;

        const handleColumnTypeIdChange = (item, e) => {
          if (
            (values.column_type_id != 3 &&
              values.column_type_id != 13 &&
              values.column_type_id != 11) ||
            (item.column_type_id != 3 &&
              item.column_type_id != 13 &&
              item.column_type_id != 11)
          ) {
            setFieldValue(
              "options",
              getDefaultColumnOptions(item.column_type_id)
            );
          }
          setFieldValue("column_type_id", item.column_type_id);
          if (item.column_type_id === "10-1") {
            setFieldValue("options.source_list_id", userListId);
            setFieldValue("options.query_string","UserAccounts.status = 'ACTIVE'");
            setFieldValue("options.is_column_updatable",true);
            setFieldValue("options.is_user_type",true);
            setFieldValue("options.column_type","text");
            setFieldValue(
              "options.source_column_id",
              userListColumns?.full_name?.replace("column_", "")
            );
          }
        };
        const commonColumnOptions = getCommonColumnOptions(values).map(
          (checkboxOption) => {
            const key = checkboxOption.value;
            const name = checkboxOption.name;
            if (!isAdditionalOptions && !checkboxOption.isShownByDefault) {
              return null;
            }
            return (
              <Grid item xs={12} key={key}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      name={`options.${key}`}
                      checked={values.options[key] || false}
                      onChange={(e) => {
                        if (key === "default_email" && e.target.checked) {
                          setFieldValue("options.allow_free_text", true);
                        } else if (key === "is_unique") {
                          if (e.target.checked) {
                            if(values.options.unique_options == null){
                              let initialUniqueVlaue = getDefaultUniqueOptions(
                                store.column?.column_name,
                                store.column?.options?.unique_options
                              );
                              
                            setFieldValue("options.unique_options", {
                              action: initialUniqueVlaue.action,
                              message: initialUniqueVlaue.message,
                            });
                            forceUpdate()
                          } else {
                            setFieldValue("options.unique_options", null);
                          }
                          }
                        }
                        handleChange(e);
                      }}
                    />
                  }
                  label={name}
                />
                {checkboxOption.value == "is_unique" &&
                  values.options[key] == true && (
                    <UniqueOptions
                      options={getDefaultUniqueOptions(
                        values.column_name,
                        values.options.unique_options
                      )}
                      handleUniqueValueChange={(action, message) => {
                        setFieldValue("options.unique_options", {
                          action: action,
                          message: message,
                        });
                      }}
                    />
                  )}
              </Grid>
            );
          }
        );

        return (
          <form onSubmit={handleSubmit}>
            <StyledDialogContent>
              <Grid container direction="column" spacing={1}>
                <Grid item container>
                  {/* <Grid item xs={6}>
                                                        Field Name
                                                    </Grid> */}
                  <Grid item xs={12}>
                    <TextFieldLarge
                      variant="outlined"
                      fullWidth
                      placeholder="Field Name"
                      autoFocus
                      value={values.column_name}
                      onChange={(e) =>
                        setFieldValue("column_name", e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSubmit(e);
                        }
                      }}
                    />
                    <StyledErrorMsg>
                      <ErrorMessage name="column_name" />
                    </StyledErrorMsg>
                  </Grid>
                </Grid>
                <Grid item container>
                  {/* <Grid item xs={6}>
                          Field Type
                        </Grid> */}
                  <Grid item xs={12}>
                    <ColumnTypesListPopup
                      value={
                        columnTypesArrayPlain.find(
                          (columnType) =>
                            columnType.column_type_id == values.column_type_id
                        )?.name
                      }
                      handleSubmit={handleSubmit}
                      onChange={handleColumnTypeIdChange}
                      isSubsheet={isSubsheet}
                    />
                  </Grid>
                </Grid>
                <StyledGrid item container direction="column" spacing={1}>
                  {/* <Grid item xs={3}>
                          Options
                        </Grid> */}
                  <Grid item container xs={12} direction="column" spacing={1}>
                    {values.column_type_id == 1 ||
                    values.column_type_id == 20 ||
                    values.column_type_id == 8 ||
                    values.column_type_id == 14 ||
                    values.column_type_id == 5 ||
                    values.column_type_id == 17 ||
                    values.column_type_id == 9 ? (
                      <>
                        {commonColumnOptions}
                        <ColumnConfigFactory
                          store={store}
                          values={values}
                          handleChange={handleChange}
                          setFieldValue={setFieldValue}
                          isAdditionalOptions={isAdditionalOptions}
                        />
                      </>
                    ) : (
                      <>
                        <ColumnConfigFactory
                          store={store}
                          values={values}
                          handleChange={handleChange}
                          setFieldValue={setFieldValue}
                          isAdditionalOptions={isAdditionalOptions}
                        />
                        {commonColumnOptions}
                      </>
                    )}
                    {commonColumnOptions.length > 0 && (
                      <Button
                        component="a"
                        onClick={() => {
                          showAdditionalOptions(!isAdditionalOptions);
                          forceUpdate();
                        }}
                      >
                        {isAdditionalOptions ? "-" : "+"} additional options
                      </Button>
                    )}
                  </Grid>
                </StyledGrid>
              </Grid>
            </StyledDialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="primary">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                color="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving " : "Save"}
                {isSubmitting && (
                  <CircularProgress
                    size="20"
                    style={{ width: 12 }}
                    color="inherit"
                  />
                )}
              </Button>
            </DialogActions>
          </form>
        );
      }}
    </Formik>
  );
}

const StyledErrorMsg = styled.div`
  color: red;
`;

const TextFieldLarge = styled(TextField)`
  input {
    font-size: 14px !important;
    height: 26px;
  }
`;

const StyledGrid = styled(Grid)`
  padding-top: 20px !important;
`;
const StyledDialogContent=styled(DialogContent)`
  max-height:70vh;
`;

export default observer(AddColumnForm);
